import React, { useEffect, useState, useRef } from 'react';
import { Popover, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { GlobeAltIcon } from "@heroicons/react/24/outline";
import { FC, Fragment } from "react";
import { useClient } from "ClientContext";
import i18next from 'i18next';

const getLanguageDetails = (code: string) => {
  switch (code) {
    case "en":
      return { code: code, name: "English", description: "United States" };
    case "es":
      return { code: code, name: "Spanish", description: "Spain" };
    case "ja":
      return { code: code, name: "Japanese", description: "Japan" };
    case "fr":
      return { code: code, name: "Français", description: "France" };
    case "de":
      return { code: code, name: "Deutsch", description: "Germany" };
    case "it":
      return { code: code, name: "Italiano", description: "Italy" };
    default:
      return { code: "en", name: "English", description: "United States" };
  }
};

interface LangDropdownProps {
  panelClassName?: string;
}

const LangDropdown: FC<LangDropdownProps> = ({
  panelClassName = "z-10 w-screen max-w-[170px] px-4 mt-4 right-0 sm:px-0",
}) => {
  const { setGlobal_language, clientName, integration_TYPE,global_language } = useClient();
  const [languages, setLanguages] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const prevClientName = useRef<string | null>(null);

  const setLanguageGlobaly = (LanguageCode: string, close: () => void) => {
    // Update the active state for languages
    setLanguages((prevLanguages: any[]) =>
      prevLanguages.map((lang) => ({
        ...lang,
        active: lang.code === LanguageCode, // Set active to true for the selected language
      }))
    );
    setGlobal_language(LanguageCode);
    i18next.changeLanguage(LanguageCode);
    close();
  };

  useEffect(() => {
    if (integration_TYPE === "guesty") {
      setLanguages([{ code: 'en', name: 'English', description: 'United States', active: true }]);
      i18next.changeLanguage('en');
      setLoading(false);
      return;
    }

    const fetchLodgifyLanguages = async () => {
      if (!clientName || clientName === 'n/a' || integration_TYPE !== "lodgify") return;

      setLoading(true);
      try {
        const response = await fetch(`https://checkout.lodgify.com/api/v1/checkout/languages?slug=${clientName}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch data: ${response.statusText}`);
        }

        const data = await response.json();
        const codes = data.languages;
        const languagesList = codes.map((code: string) => ({
          ...getLanguageDetails(code),
          href: "##",
          active: code === data.defaultLanguage,
        }));
        
       //this is to ensure that English is always available
        if (!languagesList.some((lang: any) => lang.code === 'en')) {
          languagesList.push({ ...getLanguageDetails('en'), href: "##", active: false });
        } 
        setLanguages(languagesList);
      } catch (error) {
        console.error('Error fetching Lodgify languages:', error);
        setError('Failed to fetch data.');
      } finally {
        setLoading(false);
      }
    };

    if (clientName && clientName !== prevClientName.current) {
      prevClientName.current = clientName;
      fetchLodgifyLanguages();
    }
  }, [clientName, integration_TYPE]);

  return (
    <div className="LangDropdown">
      <Popover className="relative">
        {({ open, close }) => (
          <>
            <Popover.Button
              className={`
                ${open ? "" : "text-opacity-80"}
                group px-3 py-1.5 border-neutral-300 hover:border-neutral-400 dark:border-neutral-700 rounded-full inline-flex items-center text-sm text-gray-700 dark:text-neutral-300 font-medium hover:text-opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75`}
            >
              <GlobeAltIcon className="w-[18px] h-[18px] opacity-80" />
              <span className="ml-2 select-none">{global_language}</span>
              <ChevronDownIcon
                className={`${open ? "-rotate-180" : "text-opacity-70"}
                  ml-2 h-4 w-4  group-hover:text-opacity-80 transition ease-in-out duration-150`}
                aria-hidden="true"
              />
            </Popover.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className={`absolute ${panelClassName}`}>
                <div className="overflow-hidden rounded-2xl shadow-lg ring-1 ring-black ring-opacity-5">
                  <div className="relative grid gap-8 bg-white dark:bg-neutral-800 p-7 lg:grid-cols-1">
                    {loading ? (
                      <div className="text-center">Loading...</div>
                    ) : error ? (
                      <div className="text-center text-red-500">{error}</div>
                    ) : (
                      languages.map((item: any, index: number) => (
                        <a
                          key={index}
                          href={item.href}
                          onClick={() => setLanguageGlobaly(item.code, close)}
                          className={`flex items-center p-2 -m-3 transition duration-150 ease-in-out rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 focus:outline-none focus-visible:ring focus-visible:ring-orange-500 focus-visible:ring-opacity-50 ${
                            item.active
                              ? "bg-gray-100 dark:bg-neutral-700"
                              : "opacity-90"
                          }`}
                        >
                          <div>
                            <p className="text-sm font-medium">{item.name}</p>
                          </div>
                        </a>
                      ))
                    )}
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
};
export default LangDropdown;